import React, { useState, useCallback } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { Card, CardBody, CardTitle } from 'reactstrap';
import qs from 'qs';
import _get from 'lodash/get';
import _toLower from 'lodash/toLower';
import styles from './index.module.scss';

import { If } from 'utils';
import { Loader, InfoBanner } from 'components';
import { StepOne, StepTwo } from './components';
import { selectHelpers } from 'utils/uiKitHelpers';
import { useAppContext, useFetch } from 'utils/hooks';
import { ReactComponent as InProgressIcon } from 'assets/img/icons/in-progress.svg';

export default function Login({ login = () => {} }) {
  const { search } = useLocation();
  const { replace } = useHistory();

  const { appName } = useAppContext();

  const queryParams = qs.parse(search, { ignoreQueryPrefix: true });
  const appInstall = _get(queryParams, 'app_install', '');
  const storeOwnerEmail = _get(queryParams, 'email', '');
  const storeIdentifier = _get(queryParams, 'identifier', '');
  const isAppInstall = _toLower(appInstall) === 'true';

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [retailers, setRetailers] = useState([]);
  const [isFetchingInstallEmail, setIsFetchingInstallEmail] = useState(
    isAppInstall && !storeOwnerEmail && !!storeIdentifier
  );

  const { fetchData } = useFetch('account', {
    method: 'POST',
    isPublicAPI: true,
    payload: { identifier: storeIdentifier },
    errorMessage: '',
    loadOnMount: isFetchingInstallEmail,
    onSuccess: data => {
      const redirectURL = _get(data, 'redirect_url', '');
      const userEmail = _get(data, 'email', '');
      const error = _get(data, 'error', '');
      if (redirectURL) {
        window.location.href = redirectURL;
      } else if (error) {
        replace('/login');
        setErrorMsg(error);
        setIsFetchingInstallEmail(false);
      } else if (!userEmail) {
        setTimeout(() => fetchData(), 1000);
      } else {
        const queryParams = qs.stringify({
          app_install: true,
          email: userEmail
        });
        replace(`/login?${queryParams}`);
        setIsFetchingInstallEmail(false);
      }
    },
    onError: () => {
      replace('/login?app_install=true');
      setIsFetchingInstallEmail(false);
    }
  });

  const reset = useCallback(() => {
    setStep(1);
    setRetailers([]);
  }, []);

  const proceed = useCallback(({ retailers, email }) => {
    // retailers will have seller name as id and stores list
    const validRetailers = Object.keys(retailers).reduce((acc, key) => {
      if (retailers[key].length > 0) {
        acc.push({ name: key, stores: retailers[key] });
      }
      return acc;
    }, []);

    const { options } = selectHelpers.createOptions(validRetailers, {
      getLabel: item => _get(item, 'name', ''),
      getValue: item => _get(item, 'rr_store_id', '') || _get(item, 'name', ''),
      getOptions: item => _get(item, 'stores', [])
    });

    setRetailers(options);
    setEmail(email);
    setStep(2);
  }, []);

  return (
    <div id="login-row">
      <If
        test={!errorMsg}
        otherwise={
          <InfoBanner
            icon={<InProgressIcon className={styles.infoIcon} />}
            className={styles.infoAlert}
          >
            {errorMsg}
          </InfoBanner>
        }
      >
        <Card className="login-card">
          <CardBody className="p-0">
            <If
              test={!isFetchingInstallEmail}
              otherwise={
                <div className="login__install-loader">
                  <Loader loading />
                  Setting up your account...
                </div>
              }
            >
              <CardTitle className="text-center">
                Login to your account
              </CardTitle>
              <If test={isAppInstall}>
                <div className="login__emailSent">
                  Welcome to {appName}!{' '}
                  <If
                    test={storeOwnerEmail}
                    otherwise={
                      <>An email has been sent with your credentials.</>
                    }
                  >
                    An email with your credentials has been sent to{' '}
                    <strong>{storeOwnerEmail}</strong>.
                  </If>{' '}
                  Please check your spam folder as well.
                </div>
              </If>
              <If test={step === 1}>
                <StepOne onSubmit={proceed} />
              </If>
              <If test={step === 2}>
                <StepTwo email={email} loggedIn={login} retailers={retailers} />
                <div className="text-right mt-2">
                  <span className="reset-link" onClick={reset}>
                    Reset form
                  </span>
                </div>
              </If>
            </If>
          </CardBody>
        </Card>
        <If test={!isFetchingInstallEmail}>
          <div className="text-right mt-2">
            <Link to="/reset-password" className="route-switch-link">
              Forgot your password?
            </Link>
          </div>
        </If>
      </If>
    </div>
  );
}
